// Travelling pulses, and nothing else.
//
// IMPLEMENTATION.md is explicit twice over: no random particle placement
// (rule 3), and every travelling pulse must correspond to real application
// state (rule 4). So this component draws exactly one thing — a pulse for a
// reasoning event that actually happened — and draws nothing at all in the
// healthy state. There is no ambient loop, no idle sparkle, no decorative
// motion. An empty canvas is the correct healthy render.
//
// The alpha mask clips every stroke, so a pulse cannot leak outside the
// anatomy even if an anchor were wrong.
import React, { useEffect, useRef } from "react";

/** Evidence travels department -> core; a decision travels core -> department.
 *  Direction reflects the real event, per the contract's state model. */
const EVIDENCE_MS = 1300;   // tokens.motion.reasoningPulseDurationMs
const TRAIL = 0.16;

function prefersReducedMotion() {
  return typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export default function BrainActivityCanvas({ brain, anchors, core, maskSrc }) {
  const ref = useRef(null);
  const maskRef = useRef(null);
  const rafRef = useRef(0);

  // Lazy-load the mask; the master is what Brain Home preloads.
  useEffect(() => {
    if (typeof Image === "undefined") return undefined;
    const img = new Image();
    img.src = maskSrc;
    img.onload = () => { maskRef.current = img; };
    return () => { img.onload = null; };
  }, [maskSrc]);

  const reasoning = brain && brain.reasoning;

  useEffect(() => {
    const cv = ref.current;
    if (!cv || typeof window === "undefined") return undefined;
    const ctx = cv.getContext && cv.getContext("2d");
    if (!ctx) return undefined;

    const clear = () => ctx.clearRect(0, 0, cv.width, cv.height);

    const resize = () => {
      const r = cv.getBoundingClientRect();
      const d = Math.min(window.devicePixelRatio || 1, 2);
      cv.width = Math.max(1, Math.round(r.width * d));
      cv.height = Math.max(1, Math.round(r.height * d));
    };
    resize();
    window.addEventListener("resize", resize);

    // Healthy: nothing is happening, so nothing is drawn.
    if (!reasoning || !reasoning.department) {
      clear();
      return () => { window.removeEventListener("resize", resize); };
    }

    const from = anchors.find((a) => a.id === reasoning.department);
    if (!from) {
      clear();
      return () => { window.removeEventListener("resize", resize); };
    }

    // Reduced motion: state is communicated by the node's colour, which is
    // CSS. Travel is disabled outright rather than merely slowed.
    if (prefersReducedMotion()) {
      clear();
      return () => { window.removeEventListener("resize", resize); };
    }

    const start = performance.now();
    const draw = (now) => {
      const W = cv.width, H = cv.height;
      clear();

      // Clip to the anatomy. Mask is white inside, black outside, so it is
      // painted as the destination-in source after the strokes.
      const t = ((now - start) % (EVIDENCE_MS * 2)) / EVIDENCE_MS;
      const outbound = t > 1;                 // evidence in, then decision out
      const p = outbound ? t - 1 : t;

      const ax = from.x * W, ay = from.y * H;
      const bx = core.x * W, by = core.y * H;
      const sx = outbound ? bx : ax, sy = outbound ? by : ay;
      const ex = outbound ? ax : bx, ey = outbound ? ay : by;

      const cx = sx + (ex - sx) * p, cy = sy + (ey - sy) * p;
      const tp = Math.max(0, p - TRAIL);
      const tx = sx + (ex - sx) * tp, ty = sy + (ey - sy) * tp;

      const g = ctx.createLinearGradient(cx, cy, tx, ty);
      g.addColorStop(0, "rgba(255,79,200,0.9)");
      g.addColorStop(1, "rgba(255,79,200,0)");
      ctx.lineCap = "round";
      ctx.lineWidth = Math.max(2, W * 0.004);
      ctx.strokeStyle = g;
      ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(tx, ty); ctx.stroke();

      if (maskRef.current) {
        ctx.globalCompositeOperation = "destination-in";
        ctx.drawImage(maskRef.current, 0, 0, W, H);
        ctx.globalCompositeOperation = "source-over";
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [reasoning, anchors, core]);

  return <canvas className="cb-layer cb-activity" ref={ref} data-testid="cb-activity" aria-hidden="true" />;
}
